import React, { useState } from 'react';
import { ArrowLeft, Plus, X, RotateCcw } from 'lucide-react';

const RecipePreview = ({ recipe, onSave, onBack, isUpdate = false }) => {
  const [editedRecipe, setEditedRecipe] = useState(recipe);
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState('');
  
  // Check if title was changed during update
  const originalTitle = recipe._originalTitle;
  const titleChanged = isUpdate && originalTitle && editedRecipe.title !== originalTitle;

  // Revert title to original
  const handleRevertTitle = () => {
    setEditedRecipe({ ...editedRecipe, title: originalTitle });
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...editedRecipe.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setEditedRecipe({ ...editedRecipe, ingredients: newIngredients });
  };

  const handleAddIngredient = () => {
    // Get the category from the last ingredient to continue the same section
    const lastCategory = editedRecipe.ingredients.length > 0 
      ? editedRecipe.ingredients[editedRecipe.ingredients.length - 1].category 
      : null;
    setEditedRecipe({
      ...editedRecipe,
      ingredients: [...editedRecipe.ingredients, { name: '', quantity: '', unit: '', category: lastCategory, preparation: '' }],
    });
  };

  const handleRemoveIngredient = (index) => {
    setEditedRecipe({
      ...editedRecipe,
      ingredients: editedRecipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...editedRecipe.instructions];
    newInstructions[index] = { ...newInstructions[index], text: value };
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const handleInstructionSectionChange = (index, value) => {
    const newInstructions = [...editedRecipe.instructions];
    newInstructions[index] = { ...newInstructions[index], section: value };
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const handleAddInstruction = () => {
    // Get the section from the last instruction to continue the same section
    const lastSection = editedRecipe.instructions.length > 0 
      ? editedRecipe.instructions[editedRecipe.instructions.length - 1].section 
      : null;
    setEditedRecipe({
      ...editedRecipe,
      instructions: [
        ...editedRecipe.instructions,
        { step_number: editedRecipe.instructions.length + 1, text: '', section: lastSection },
      ],
    });
  };

  const handleRemoveInstruction = (index) => {
    const newInstructions = editedRecipe.instructions
      .filter((_, i) => i !== index)
      .map((inst, i) => ({ ...inst, step_number: i + 1 }));
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const handleSave = async () => {
    if (!editedRecipe.title.trim()) {
      alert('Please enter a recipe title');
      return;
    }
    if (editedRecipe.ingredients.length === 0) {
      alert('Please add at least one ingredient');
      return;
    }
    if (editedRecipe.instructions.length === 0) {
      alert('Please add at least one instruction');
      return;
    }

    setSaving(true);
    setSavingStatus('Saving recipe...');
    try {
      console.log('Saving recipe:', editedRecipe);
      await onSave(editedRecipe);
      setSavingStatus('Saved!');
    } catch (error) {
      console.error('Save error:', error);
      setSavingStatus('Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                data-testid="back-to-chat-button"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title={isUpdate ? "Back to Recipe" : "Continue editing with AI"}
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {isUpdate ? 'Review Changes' : 'Review Recipe'}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onBack}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium hidden sm:block"
              >
                {isUpdate ? 'Cancel' : 'Continue Editing with AI'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-recipe-button"
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {saving ? 'Saving...' : (isUpdate ? 'Save Changes' : 'Add to Honey')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title Change Notice */}
        {titleChanged && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-medium text-amber-800">Title Changed</p>
                <p className="text-sm text-amber-700">
                  Original: "<span className="font-medium">{originalTitle}</span>" → 
                  New: "<span className="font-medium">{editedRecipe.title}</span>"
                </p>
              </div>
            </div>
            <button
              onClick={handleRevertTitle}
              className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
            >
              <RotateCcw size={16} />
              Revert
            </button>
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipe Title *</label>
            <div className="relative">
              <input
                type="text"
                value={editedRecipe.title}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, title: e.target.value })}
                data-testid="recipe-title-input"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                  titleChanged ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
                }`}
                placeholder="Enter recipe title"
              />
              {titleChanged && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-amber-600 font-medium">
                  Changed
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={editedRecipe.description || ''}
              onChange={(e) => setEditedRecipe({ ...editedRecipe, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Brief description of the recipe"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prep (min)</label>
              <input
                type="number"
                value={editedRecipe.prep_time || ''}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, prep_time: parseInt(e.target.value) || null })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cook (min)</label>
              <input
                type="number"
                value={editedRecipe.cook_time || ''}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, cook_time: parseInt(e.target.value) || null })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Servings</label>
              <input
                type="number"
                value={editedRecipe.servings || ''}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, servings: parseInt(e.target.value) || null })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={editedRecipe.category || 'other'}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="main_dish">Main Dish</option>
                <option value="appetizer">Appetizer</option>
                <option value="dessert">Dessert</option>
                <option value="salad">Salad</option>
                <option value="soup">Soup</option>
                <option value="side">Side</option>
                <option value="breakfast">Breakfast</option>
                <option value="snack">Snack</option>
                <option value="beverage">Beverage</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Ingredients</h2>
            <button
              onClick={handleAddIngredient}
              className="flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add
            </button>
          </div>
          <div className="space-y-3">
            {(() => {
              let currentCategory = null;
              return editedRecipe.ingredients.map((ing, idx) => {
                const showHeader = ing.category && ing.category !== currentCategory;
                currentCategory = ing.category || currentCategory;
                return (
                  <React.Fragment key={idx}>
                    {showHeader && (
                      <h4 className="font-bold text-gray-700 mt-4 mb-2 pt-3 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0">
                        {ing.category}
                      </h4>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={ing.quantity || ''}
                        onChange={(e) => handleIngredientChange(idx, 'quantity', e.target.value)}
                        placeholder="Amount"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                      <input
                        type="text"
                        value={ing.unit || ''}
                        onChange={(e) => handleIngredientChange(idx, 'unit', e.target.value)}
                        placeholder="Unit"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                      <input
                        type="text"
                        value={ing.name}
                        onChange={(e) => handleIngredientChange(idx, 'name', e.target.value)}
                        placeholder="Ingredient name"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                      <input
                        type="text"
                        value={ing.preparation || ''}
                        onChange={(e) => handleIngredientChange(idx, 'preparation', e.target.value)}
                        placeholder="Prep (e.g., chopped)"
                        className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm text-gray-500"
                        title="Preparation method (e.g., chopped, minced, sliced)"
                      />
                      <input
                        type="text"
                        value={ing.category || ''}
                        onChange={(e) => handleIngredientChange(idx, 'category', e.target.value)}
                        placeholder="Section"
                        className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm text-gray-500"
                        title="Section header (e.g., 'For the Brownies')"
                      />
                      <button
                        onClick={() => handleRemoveIngredient(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Instructions</h2>
            <button
              onClick={handleAddInstruction}
              className="flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add Step
            </button>
          </div>
          <div className="space-y-3">
            {(() => {
              let currentSection = null;
              return editedRecipe.instructions.map((inst, idx) => {
                const showHeader = inst.section && inst.section !== currentSection;
                currentSection = inst.section || currentSection;
                return (
                  <React.Fragment key={idx}>
                    {showHeader && (
                      <h4 className="font-bold text-amber-700 mt-4 mb-2 pt-3 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0">
                        {inst.section}
                      </h4>
                    )}
                    <div className="flex gap-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center font-semibold text-amber-600">
                        {inst.step_number}
                      </div>
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={inst.text}
                          onChange={(e) => handleInstructionChange(idx, e.target.value)}
                          placeholder="Describe this step..."
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        />
                        <input
                          type="text"
                          value={inst.section || ''}
                          onChange={(e) => handleInstructionSectionChange(idx, e.target.value)}
                          placeholder="Section (e.g., 'Prepare the Brownies')"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm text-gray-500"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveInstruction(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors self-start"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecipePreview;